import { useState, useEffect } from 'react';
import { Modal, Button, Input, Dropdown } from 'semantic-ui-react';
import MarkdownTextarea from 'src/components/common/markdown-textarea';
import { ModalContainer } from './styles/EntryModalStyles';
import {
  typeOptions,
  requiresLink,
  requiresContent,
  getLinkPlaceholder,
  getLinkLabel,
  getContentLabel,
  validateEntryForm,
} from './utils/entryModalUtils';

const EntryModal = ({ open, onClose, entry, onSave }) => {
  const [inputs, setInputs] = useState({
    title: '',
    type: 'text',
    content: '',
    link: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (entry) {
        // Editing existing entry
        setInputs({
          title: entry.title || '',
          type: entry.type || 'text',
          content: entry.content || '',
          link: entry.link || '',
          date: entry.date
            ? new Date(entry.date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
        });
      } else {
        // Creating new entry
        setInputs({
          title: '',
          type: 'text',
          content: '',
          link: '',
          date: new Date().toISOString().split('T')[0],
        });
      }
      setErrors({});
    }
  }, [open, entry]);

  const handleInputs = (event, data) => {
    const name = data?.name || event.target.name;
    const value = data?.value !== undefined ? data.value : event.target.value;

    setInputs((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = validateEntryForm(inputs);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setInputs({
      title: '',
      type: 'text',
      content: '',
      link: '',
      date: new Date().toISOString().split('T')[0],
    });
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const entryData = {
        id: entry?.id,
        title: inputs.title.trim(),
        type: inputs.type,
        content: inputs.content.trim() || null,
        link: inputs.link.trim() || null,
        date: new Date(inputs.date).toISOString(), // Keep for frontend display, backend doesn't use it
      };

      await onSave(entryData);
      resetForm();
      onClose();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="large">
      <Modal.Header>{entry ? 'Editar Entrada' : 'Crear Nueva Entrada'}</Modal.Header>
      <Modal.Content>
        <ModalContainer>
          <div className="form-field">
            <label htmlFor="title">Título *</label>
            <Input
              id="title"
              name="title"
              value={inputs.title}
              onChange={handleInputs}
              placeholder="Ingresa el título de la entrada"
              error={!!errors.title}
            />
            {errors.title && <div className="error">{errors.title}</div>}
          </div>

          <div className="form-field">
            <label htmlFor="type">Tipo *</label>
            <Dropdown
              id="type"
              name="type"
              selection
              options={typeOptions}
              value={inputs.type}
              onChange={(e, data) => handleInputs(e, data)}
              placeholder="Selecciona el tipo de entrada"
              error={!!errors.type}
            />
            {errors.type && <div className="error">{errors.type}</div>}
          </div>

          {requiresLink(inputs.type) && (
            <div className="form-field">
              <label htmlFor="link">{getLinkLabel(inputs.type)}</label>
              <Input
                id="link"
                name="link"
                value={inputs.link}
                onChange={handleInputs}
                placeholder={getLinkPlaceholder(inputs.type)}
                error={!!errors.link}
              />
              {errors.link && <div className="error">{errors.link}</div>}
            </div>
          )}

          {requiresContent(inputs.type) && (
            <div className="form-field">
              <MarkdownTextarea
                inputs={inputs}
                name="content"
                label={getContentLabel(inputs.type)}
                handleInputs={handleInputs}
                errorInputs={errors}
                loading={loading}
                showHelpers={true}
              />
              {errors.content && <div className="error">{errors.content}</div>}
            </div>
          )}

          <div className="modal-actions">
            <Button onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button primary onClick={handleSubmit} loading={loading} disabled={loading}>
              {entry ? 'Guardar Cambios' : 'Crear Entrada'}
            </Button>
          </div>
        </ModalContainer>
      </Modal.Content>
    </Modal>
  );
};

export default EntryModal;
